import type { PaymentConfirmStatus, PaymentValidateResult } from '@/types/models'

export type ConfirmOutcome = 'confirmed' | 'timeout' | 'unconfirmed' | 'aborted'

export interface ConfirmOptions {
  /** Backend validate call: confirmed / recovered end it; timeout fails it; waiting says how long to poll. */
  validate: () => Promise<PaymentValidateResult>
  status: () => Promise<PaymentConfirmStatus>
  /** Poll spacing while waiting (mobile: 2 s). */
  pollMs?: number
  /** Give up after this long overall; undefined = keep going until validate says timeout. */
  deadlineMs?: number
  /** Seconds left in the current waiting window, for a countdown. */
  onCountdown?: (seconds: number) => void
  signal?: AbortSignal
  now?: () => number
  sleep?: (ms: number) => Promise<void>
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms))

// validate → confirmed/recovered: done · timeout: failed · waiting: poll every 2 s for `remainingSeconds`, then validate again.
// An overall deadline yields `unconfirmed` ("if money was deducted it will settle shortly"), not a failure.
export async function confirmPayment({ validate, status, pollMs = 2000, deadlineMs, onCountdown, signal, now = Date.now, sleep = defaultSleep }: ConfirmOptions): Promise<ConfirmOutcome> {
  const started = now()
  const expired = () => deadlineMs !== undefined && now() - started >= deadlineMs
  while (true) {
    if (signal?.aborted) return 'aborted'
    const v = await validate()
    if (v.confirmed || v.code === 'confirmed' || v.code === 'recovered') return 'confirmed'
    if (v.code === 'timeout') return 'timeout'
    const windowSeconds = Math.max(v.remainingSeconds || 10, 2)
    const windowEnd = now() + windowSeconds * 1000
    while (now() < windowEnd) {
      if (signal?.aborted) return 'aborted'
      if (expired()) return 'unconfirmed'
      onCountdown?.(Math.max(0, Math.ceil((windowEnd - now()) / 1000)))
      await sleep(pollMs)
      try {
        if ((await status()).confirmed) return 'confirmed'
      } catch {
        // A blip while polling is not a verdict; validate decides.
      }
    }
    if (expired()) return 'unconfirmed'
  }
}

export async function pollUntil<T>(fetch: () => Promise<T>, done: (r: T) => boolean, { intervalMs = 5000, maxAttempts = 20, signal, sleep = defaultSleep, onNearTimeout, nearTimeoutAttempts = 24 }: { intervalMs?: number; maxAttempts?: number; signal?: AbortSignal; sleep?: (ms: number) => Promise<void>; onNearTimeout?: () => void; nearTimeoutAttempts?: number } = {}): Promise<T | null> {
  let last: T | null = null
  for (let i = 0; i < maxAttempts; i++) {
    if (signal?.aborted) return last
    if (onNearTimeout && i === maxAttempts - nearTimeoutAttempts) onNearTimeout()
    last = await fetch()
    if (done(last)) return last
    await sleep(intervalMs)
  }
  return last
}
